import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  Search,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronDown,
} from "lucide-react";
import Typography from "./Typography";
import Button from "./Button";
import CustomInput from "./CustomInput";
import Loading from "./Loading";

export interface Column<T> {
  id: keyof T;
  label: string;
  minWidth?: number;
  align?: "left" | "right" | "center";
  format?: (value: any, row: T) => React.ReactNode;
  sortable?: boolean;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  error?: string | null;
  searchable?: boolean;
  searchPlaceholder?: string;
  searchFields?: (keyof T)[];
  search?: { value: string; onChange: (value: string) => void; disabled?: boolean; title?: string };
  toolbar?: React.ReactNode;
  stale?: boolean;
  pagination?: {
    totalCount: number;
    page: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
  };
  onRefresh?: () => void;
  emptyMessage?: string;
  showTitle?: boolean;
  title?: string;
  actions?: React.ReactNode;
  /** Opt-in, current-page row selection. Existing table consumers are unchanged. */
  selection?: {
    selectedIds: Set<string>;
    onSelectedIdsChange: React.Dispatch<React.SetStateAction<Set<string>>>;
    isRowSelectable?: (row: T) => boolean;
    getRowId?: (row: T) => string;
    renderBulkActions?: (selectedRows: T[]) => React.ReactNode;
  };
}

export function DataTable<T extends Record<string, any>>({
  columns,
  data,
  loading = false,
  error = null,
  searchable = true,
  searchPlaceholder = "Search...",
  searchFields,
  search,
  toolbar,
  stale = false,
  pagination,
  onRefresh,
  emptyMessage = "No data available",
  showTitle = false,
  title,
  actions,
  selection,
}: DataTableProps<T>) {
  const [searchTerm, setSearchTerm] = useState("");

  const pageCount = pagination ? Math.ceil(pagination.totalCount / pagination.pageSize) : 0;
  const pageWindowStart = pagination
    ? Math.max(0, Math.min(pagination.page - 2, Math.max(0, pageCount - 5)))
    : 0;
  const clampRequest = useRef<string | null>(null);
  useEffect(() => {
    if (!pagination || loading) return;
    const { page, pageSize, totalCount, onPageChange } = pagination;
    if (totalCount > 0 && page * pageSize >= totalCount) {
      const key = `${page}:${pageSize}:${totalCount}`;
      if (clampRequest.current !== key) {
        clampRequest.current = key;
        onPageChange(pageCount - 1);
      }
    } else {
      clampRequest.current = null;
    }
  }, [pagination, pageCount, loading]);

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (search || !searchable || !searchTerm.trim()) {
      return data;
    }

    return data.filter((row) => {
      const searchableFields = searchFields || columns.map((col) => col.id);
      return searchableFields.some((field) => {
        const value = row[field];
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(searchTerm.toLowerCase());
      });
    });
  }, [data, searchTerm, searchable, searchFields, columns, search]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (search) search.onChange(event.target.value);
    else setSearchTerm(event.target.value);
  };

  const formatCellValue = (column: Column<T>, value: any, row: T) => {
    if (column.format) {
      return column.format(value, row);
    }
    return value;
  };

  const getRowId = (row: T) =>
    selection?.getRowId?.(row) || String(row.id || row.attempt_id || row.user_id);
  const isRowSelectable = (row: T) => selection?.isRowSelectable?.(row) ?? true;
  const selectableRows = selection
    ? filteredData.filter((row) => isRowSelectable(row))
    : [];
  const selectedRows = selection
    ? filteredData.filter((row) => selection.selectedIds.has(getRowId(row)))
    : [];
  const allSelectableRowsSelected =
    selectableRows.length > 0 &&
    selectableRows.every((row) => selection?.selectedIds.has(getRowId(row)));

  const toggleAllVisibleRows = () => {
    if (!selection) return;
    selection.onSelectedIdsChange((current) => {
      const next = new Set(current);
      const allCurrentlySelected =
        selectableRows.length > 0 &&
        selectableRows.every((row) => next.has(getRowId(row)));
      if (allCurrentlySelected) {
        selectableRows.forEach((row) => next.delete(getRowId(row)));
      } else {
        selectableRows.forEach((row) => next.add(getRowId(row)));
      }
      return next;
    });
  };

  const toggleRow = (row: T) => {
    if (!selection || !isRowSelectable(row)) return;
    const id = getRowId(row);
    selection.onSelectedIdsChange((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const getAlignClass = (align?: "left" | "right" | "center") => {
    switch (align) {
      case "center":
        return "text-center";
      case "right":
        return "text-right";
      default:
        return "text-left";
    }
  };

  // Custom dropdown component for pagination
  const CustomDropdown = ({
    value,
    onChange,
    options,
  }: {
    value: number;
    onChange: (value: number) => void;
    options: number[];
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleToggle = () => {
      setIsOpen(!isOpen);
    };

    const handleOptionClick = (option: number) => {
      onChange(option);
      setIsOpen(false);
    };

    return (
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          aria-label="Rows per page"
          aria-expanded={isOpen}
          onClick={handleToggle}
          className="flex flex-wrap items-center justify-between gap-3 w-16 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 cursor-pointer"
        >
          <span>{value}</span>
          <ChevronDown size={14} className="text-gray-400" />
        </button>

        {isOpen && (
          <div className="absolute top-full left-0 mt-1 w-16 bg-white border border-gray-300 rounded shadow-lg z-50">
            {options.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => handleOptionClick(option)}
                className={`w-full px-3 py-1.5 text-sm text-left hover:bg-gray-50 first:rounded-t last:rounded-b ${
                  value === option
                    ? "bg-green-50 text-green-600"
                    : "text-gray-700"
                }`}
              >
                {option}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (error) {
    return (
      <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
        <Typography variant="p" color="error">
          {error}
        </Typography>
      </div>
    );
  }

  return (
    <div className="w-full rounded-lg border border-gray-200 bg-white shadow-sm">
      {/* Header */}
      {(title || actions || searchable || toolbar || onRefresh || selection?.renderBulkActions) && (
        <div className="border-b border-gray-200 bg-gray-50 p-4">
          <div className="flex flex-wrap justify-between items-center gap-4 max-sm:flex-col max-sm:items-start">
            <div className="flex flex-wrap items-center gap-4 flex-1 max-sm:w-full max-sm:min-w-0">
              {showTitle && title && (
                <Typography variant="h5" weight="semibold" color="default">
                  {title}
                </Typography>
              )}
              {showTitle && onRefresh && (
                <Button
                  variant="text"
                  startIcon={<RefreshCw size={18} />}
                  onClick={onRefresh}
                  disabled={loading}
                  className="!text-green-600 hover:!bg-green-50"
                />
              )}
              {searchable && (
                <div className="min-w-[300px] max-w-[400px] max-sm:w-full max-sm:min-w-0">
                  <CustomInput
                    placeholder={searchPlaceholder}
                    value={search?.value ?? searchTerm}
                    disabled={search?.disabled}
                    title={search?.title}
                    aria-label={searchPlaceholder}
                    onKeyDown={(event) => { if (event.key === "Escape") { if (search) search.onChange(""); else setSearchTerm(""); } }}
                    onChange={handleSearchChange}
                    icon={<Search size={18} />}
                    className={{
                      container: "w-full",
                      input:
                        "bg-white border-gray-300 hover:border-green-500 focus:border-green-500 focus:ring-green-500 pl-10",
                      icon: "absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400",
                    }}
                  />
                </div>
              )}
              {toolbar}
            </div>
            <div className="flex flex-wrap items-center gap-3 max-sm:w-full max-sm:flex-wrap">
              {selection?.renderBulkActions?.(selectedRows)}
              {actions && <div className="flex items-center">{actions}</div>}
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="max-h-[600px] overflow-y-auto overflow-x-auto">
        <table className="w-full">
          <thead className="sticky top-0 bg-gray-50">
            <tr className="border-b-2 border-gray-200">
              {selection && (
                <th className="px-4 py-3 text-center">
                  <input
                    type="checkbox"
                    aria-label="Select all visible users"
                    checked={allSelectableRowsSelected}
                    disabled={selectableRows.length === 0}
                    onChange={toggleAllVisibleRows}
                    className="h-4 w-4 cursor-pointer accent-green-600 disabled:cursor-not-allowed"
                  />
                </th>
              )}
              {columns.map((column) => (
                <th
                  key={String(column.id)}
                  className={`px-4 py-3 font-semibold text-gray-700 text-sm uppercase tracking-wider ${getAlignClass(column.align)}`}
                  style={{ minWidth: column.minWidth }}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className={stale ? "opacity-50" : undefined} data-stale={stale || undefined}>
            {loading ? (
              <tr>
                <td
                  colSpan={columns.length + (selection ? 1 : 0)}
                  className="px-4 py-8 text-center border-b border-gray-100"
                >
                  <div className="flex flex-col items-center gap-3">
                    <Loading />
                    <Typography variant="p" color="default">
                      Loading data...
                    </Typography>
                  </div>
                </td>
              </tr>
            ) : filteredData?.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (selection ? 1 : 0)}
                  className="px-4 py-8 text-center border-b border-gray-100"
                >
                  <div className="flex flex-col items-center gap-2">
                    <Typography variant="p" weight="medium" color="default">
                      {!search && searchTerm ? "No results found" : emptyMessage}
                    </Typography>
                    {!search && searchTerm && (
                      <Typography variant="small" color="default">
                        Try adjusting your search terms
                      </Typography>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              filteredData?.map((row, index) => (
                <tr
                  key={row?.id || row?.attempt_id || row?.user_id || index}
                  className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                >
                  {selection && (
                    <td className="px-4 py-3 text-center">
                      <input
                        type="checkbox"
                        aria-label={`Select ${String(row.full_name || row.name || row.email || getRowId(row))}`}
                        checked={selection.selectedIds.has(getRowId(row))}
                        disabled={!isRowSelectable(row)}
                        onChange={() => toggleRow(row)}
                        className="h-4 w-4 cursor-pointer accent-green-600 disabled:cursor-not-allowed"
                      />
                    </td>
                  )}
                  {columns.map((column) => (
                    <td
                      key={String(column.id)}
                      className={`px-4 py-3 text-gray-700 text-sm ${getAlignClass(column.align)}`}
                    >
                      {formatCellValue(column, row[column.id], row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && (
        <div className="border-t border-gray-200 bg-gray-50 px-4 py-3 relative">
          <div className="flex flex-wrap items-center justify-between max-sm:flex-col max-sm:items-start max-sm:gap-3">
            <div className="flex flex-wrap items-center gap-6 max-sm:flex-wrap max-sm:gap-3">
              <Typography
                variant="small"
                color="default"
                className="whitespace-nowrap !text-grey-600"
              >
                {pagination.totalCount === 0 ? "0 results" : <>Showing {pagination.page * pagination.pageSize + 1} to{" "}
                {Math.min(
                  (pagination.page + 1) * pagination.pageSize,
                  pagination.totalCount
                )}{" "}
                of {pagination.totalCount} results</>}
              </Typography>
              <span aria-live="polite" className="text-sm whitespace-nowrap">
                Page {pageCount === 0 ? 0 : pagination.page + 1} of {pageCount}
              </span>
              <div className="flex items-center gap-2">
                <Typography
                  variant="small"
                  color="default"
                  className="whitespace-nowrap !text-grey-600"
                >
                  Rows per page:
                </Typography>
                <CustomDropdown
                  value={pagination.pageSize}
                  onChange={pagination.onPageSizeChange}
                  options={[5, 10, 25, 50, 100]}
                />
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="text"
                ariaLabel="First page"
                startIcon={<ChevronsLeft size={14} />}
                onClick={() => pagination.onPageChange(0)}
                disabled={pageCount === 0 || pagination.page === 0}
                className="!text-gray-600 hover:!bg-gray-100 !p-1 !min-w-[32px] !h-8"
              />
              <Button
                variant="text"
                ariaLabel="Previous page"
                startIcon={<ChevronLeft size={14} />}
                onClick={() => pagination.onPageChange(pagination.page - 1)}
                disabled={pageCount === 0 || pagination.page === 0}
                className="!text-gray-600 hover:!bg-gray-100 !p-1 !min-w-[32px] !h-8"
              />
              <div className="flex items-center gap-0.5 mx-2">
                {Array.from(
                  {
                    length: Math.min(
                      5,
                      Math.ceil(pagination.totalCount / pagination.pageSize)
                    ),
                  },
                  (_, i) => {
                    const pageNumber = pageWindowStart + i + 1;
                    const isCurrentPage = pageNumber === pagination.page + 1;
                    return (
                      <Button
                        key={pageNumber}
                        ariaLabel={`Page ${pageNumber}`}
                        aria-current={isCurrentPage ? "page" : undefined}
                        variant={isCurrentPage ? "filled" : "text"}
                        onClick={() => pagination.onPageChange(pageNumber - 1)}
                        className={`!min-w-[28px] !h-8 !p-0 !text-sm ${
                          isCurrentPage
                            ? "!bg-primary !text-white"
                            : "!text-gray-600 hover:!bg-gray-100"
                        }`}
                      >
                        {pageNumber}
                      </Button>
                    );
                  }
                )}
              </div>
              <Button
                variant="text"
                ariaLabel="Next page"
                endIcon={<ChevronRight size={14} />}
                onClick={() => pagination.onPageChange(pagination.page + 1)}
                disabled={
                  (pagination.page + 1) * pagination.pageSize >=
                  pagination.totalCount
                }
                className="!text-gray-600 hover:!bg-gray-100 !p-1 !min-w-[32px] !h-8"
              />
              <Button
                variant="text"
                ariaLabel="Last page"
                endIcon={<ChevronsRight size={14} />}
                onClick={() =>
                  pagination.onPageChange(
                    Math.ceil(pagination.totalCount / pagination.pageSize) - 1
                  )
                }
                disabled={
                  (pagination.page + 1) * pagination.pageSize >=
                  pagination.totalCount
                }
                className="!text-gray-600 hover:!bg-gray-100 !p-1 !min-w-[32px] !h-8"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataTable;
